import { type Theme } from '@doxyme/ui/theme';
import { type CSSObject } from '@emotion/react';

type UseStylesReturnType = Record<'container' | 'default', CSSObject>;

export const useVirtualBackgroundContainerStyles = (
  theme: Theme,
): UseStylesReturnType => ({
  container: {
    display: 'flex',
    minWidth: 100,
    maxWidth: 136,
    minHeight: 80,
    padding: theme.spacing(2),
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(1),
    border: 'none',
    borderRadius: theme.shape.radius8,
    textAlign: 'center',
    overflow: 'hidden',
    cursor: 'pointer',
    backgroundColor: theme.virtualBackground.setting.default,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    transform: 'scaleX(-1)',
    outline: `4px solid transparent`,
    outlineOffset: `-4px`,
    flex: '1 0 0',
    '&::before': {
      content: '""',
      width: '100%',
      height: '100%',
      position: 'absolute',
      left: 0,
      top: 0,
      background: `linear-gradient(0deg, ${theme.virtualBackground.image.hover} 0%, ${theme.virtualBackground.image.hover} 100%) 50% / cover no-repeat`,
      opacity: 0,
    },
    '&[aria-pressed="true"]': {
      outlineColor: theme.border.interactive.default,
      backgroundColor: theme.virtualBackground.image.selected,
      transition: 'outline-color 0.25s ease-out',

      '&::before': {
        background: `linear-gradient(0deg, ${theme.virtualBackground.image.selected} 0%, ${theme.virtualBackground.image.selected} 100%) 50% / cover no-repeat`,
        opacity: 1,
        zIndex: -1,
      },
    },
    '&:focus:not([aria-pressed="true"])::before': {
      opacity: 1,
      transition: 'opacity 0.25s ease-out',
    },
    '&:hover:not([aria-pressed="true"])::before': {
      opacity: 1,
      transition: 'opacity 0.25s ease-out',
    },
  },
  default: {
    transform: 'scaleX(1)',
    '&[aria-pressed="true"]': {
      outlineColor: theme.border.interactive.default,
      backgroundColor: theme.virtualBackground.setting.selected,
      transition: 'outline-color 0.25s ease-out',

      '&::before': {
        backgroundColor: theme.virtualBackground.setting.selected,
        opacity: 1,
        zIndex: -1,
      },
    },
    '&:hover, &:focus': {
      backgroundColor: theme.virtualBackground.setting.hover,
      transition: 'background-color 0.25s ease-out',
    },
  },
});
